import { useEffect, useState } from "react";
import { Field, Input, Label, Switch } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { useAppSelector } from "../../app/hooks";
import { handleEnterKey, handleEnterSpaceKey } from "../../app/keyboardUtils";
import Avatar from "../../components/Avatar";
import Password from "../../components/input/Password";
import SimpleListbox, { SimpleOption } from "../../components/input/SimpleListbox";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import RequiredField from "../../components/RequiredField";
import { useGetGradesQuery, useGetScoringLevelsQuery } from "../lookup/lookupApiSlice";
import { useRemoveAvatarMutation } from "../account/accountApiSlice";
import {
	Ethnicity,
	Gender,
	ProctorUserModel,
	useEditStudentInfoQuery,
} from "../users/usersApiSlice";
import ResetStudentPasswordButton from "./ResetStudentPasswordButton";

const YesNoOptions: SimpleOption[] = [
	{ value: "", label: "--" },
	{ value: "true", label: "Yes" },
	{ value: "false", label: "No" },
];

interface Props {
	studentId: number;
	updateStudentModel: (model: ProctorUserModel) => void;
	disableConfirm: (disable: boolean) => void;
}

export default function EditStudentForm(props: Props) {
	const { config } = useAppSelector((state) => state.site);
	const todayDate = new Date().toISOString().slice(0, 10);

	const [model, setModel] = useState<ProctorUserModel>();

	const [gradeOptions, setGradeOptions] = useState<SimpleOption[]>([]);
	const [scoringLevelOptions, setScoringLevelOptions] = useState<SimpleOption[]>([]);
	const [genderOptions, setGenderOptions] = useState<SimpleOption[]>([]);
	const [ethnicityOptions, setEthnicityOptions] = useState<SimpleOption[]>([]);

	const [enableUserImages, setEnableUserImages] = useState(true);
	const [hasImage, setHasImage] = useState(false);
	const [fName, setFName] = useState("");
	const [lName, setLName] = useState("");
	const [externalId, setExternalId] = useState("");
	const [dateOfBirth, setDateOfBirth] = useState("");
	const [grade, setGrade] = useState<number>(383);
	const [scoringLevel, setScoringLevel] = useState<number>(1);
	const [userName, setUserName] = useState("");
	const [password, setPassword] = useState("");
	const [showDemographicSection, setShowDemographicSection] = useState(false);
	const [showBadDataWarning, setShowBadDataWarning] = useState(false);
	const [resetPasswordError, setResetPasswordError] = useState("");

	const studentInfo = useEditStudentInfoQuery(props.studentId);
	const studentInfoResult = studentInfo.data;

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const scoringLevelResult = useGetScoringLevelsQuery();
	const scoringLevels = scoringLevelResult.data;

	const [removeUserImage] = useRemoveAvatarMutation();

	useEffect(() => {
		if (model) props.updateStudentModel(model);
	}, [model]);

	useEffect(() => {
		const isDateAllowed = dateOfBirth && dateOfBirth <= todayDate;
		if (fName && lName && isDateAllowed) {
			setShowBadDataWarning(false);
			props.disableConfirm(false);
		} else {
			setShowBadDataWarning(true);
			props.disableConfirm(true);
		}
	}, [fName, lName, dateOfBirth]);

	useEffect(() => {
		if (model) {
			const newModel: ProctorUserModel = {
				...model,
				fName: fName ?? null,
				lName: lName ?? null,
				allowUserImages: enableUserImages,
				externalID: externalId,
				dateOfBirth,
				gradeId: grade,
				scoringLevelId: scoringLevel,
				userName,
				password,
			};
			setModel(newModel);
		}
	}, [
		fName,
		lName,
		enableUserImages,
		externalId,
		dateOfBirth,
		grade,
		scoringLevel,
		userName,
		password,
	]);

	useEffect(() => {
		if (studentInfoResult) {
			setEnableUserImages(studentInfoResult.allowUserImages);
			setHasImage(studentInfoResult.hasImage);
			setFName(studentInfoResult.fName ?? "");
			setLName(studentInfoResult.lName ?? "");
			setExternalId(studentInfoResult.externalID ?? "");
			setDateOfBirth(
				studentInfoResult.dateOfBirth
					? new Date(studentInfoResult.dateOfBirth).toISOString().slice(0, 10)
					: ""
			);
			setGrade(studentInfoResult.gradeId);
			setScoringLevel(studentInfoResult.scoringLevelId);
			setUserName(studentInfoResult.userName);
			setPassword(studentInfoResult.password);
		}
		setModel(studentInfoResult);
	}, [studentInfoResult]);

	useEffect(() => {
		const gradeOptions: SimpleOption[] =
			grades?.map((grade) => {
				return {
					value: grade.gradeId,
					label: grade.name,
				} as SimpleOption;
			}) ?? [];
		setGradeOptions(gradeOptions);
	}, [grades]);

	useEffect(() => {
		const scoringLevelOptions: SimpleOption[] =
			scoringLevels?.map((level) => {
				return {
					value: level.scoringLevelId,
					label: level.name,
				} as SimpleOption;
			}) ?? [];
		setScoringLevelOptions(scoringLevelOptions);
	}, [scoringLevels]);

	useEffect(() => {
		const genderOptions: SimpleOption[] = [{ value: "", label: "--" }];
		Object.entries(Gender).map(([key, value]) => {
			genderOptions.push({ value: key, label: value });
		});
		setGenderOptions(genderOptions);

		const ethnicityOptions: SimpleOption[] = [{ value: "", label: "--" }];
		Object.entries(Ethnicity).map(([key, value]) => {
			ethnicityOptions.push({ value: key, label: value });
		});
		setEthnicityOptions(ethnicityOptions);
	}, []);

	function handleRemoveUserImage() {
		removeUserImage({ userId: props.studentId })
			.unwrap()
			.then(() => {
				setHasImage(false);
			});
	}

	function toggleShowDemographicSection() {
		setShowDemographicSection(!showDemographicSection);
	}

	return (
		<section>
			<div>
				<div className="flex justify-between pt-2">
					<h3 className="text-base font-semibold text-gray-900">Account Information</h3>
					<p className="text-xs leading-6 text-gray-600">
						<RequiredField className="!text-sm" /> - Denotes a required field
					</p>
				</div>
				{showBadDataWarning && studentInfo.isSuccess && (
					<AlertMessage
						className="mt-4 text-sm"
						type={AlertLevel.Warning}
						message={
							"Please ensure all required fields are complete and that the date of birth is today or earlier."
						}
						showIcon={true}
					/>
				)}
				{resetPasswordError && (
					<AlertMessage
						className="mt-4 text-sm"
						type={AlertLevel.Error}
						message={resetPasswordError}
						showIcon={true}
					/>
				)}
				<div className="pb-2 pt-1 grid grid-cols-1 gap-y-1 gap-x-3 sm:grid-cols-2">
					{config?.enableAvatarImages && (
						<Field className="col-span-2">
							<Label htmlFor="userAvatar" className="font-semibold text-sm">
								User Avatar
							</Label>
							<div className="mt-2 mb-1 flex items-center gap-x-3">
								<div className="relative inline-block">
									<Avatar
										className="h-12 w-12 !ml-4"
										hasImage={hasImage ?? false}
										initials={studentInfoResult?.initials ?? ""}
										userId={props.studentId}
									/>
									{hasImage ? (
										<XCircleIcon
											tabIndex={0}
											className="mx-auto h-8 w-8 fill-red-700 stroke-white absolute -bottom-2 -right-2 hover:cursor-pointer"
											onClick={() => handleRemoveUserImage()}
											onKeyDown={(e) =>
												handleEnterKey(e, handleRemoveUserImage)
											}
										/>
									) : null}
								</div>

								<div className="mt-1">
									<div className="ml-2">
										<div className="flex items-center">
											<label className="sr-only">User Avatar Toggle</label>
											<Switch
												checked={enableUserImages}
												name="userAvatar"
												className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600"
												onChange={setEnableUserImages}>
												<span
													aria-hidden="true"
													className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
												/>
											</Switch>

											<p className="ml-2 text-sm">
												{enableUserImages
													? `User avatars are enabled for ${studentInfoResult?.fName}.`
													: `User avatars are disabled for ${studentInfoResult?.fName}.`}
											</p>
										</div>

										<p className="mt-1.5 text-xs">
											{!studentInfoResult?.allowUserImages &&
												`Disabled by: ${studentInfoResult?.userImageDisabledBy}`}
										</p>
									</div>
								</div>
							</div>
						</Field>
					)}
					<Field>
						<Label htmlFor="first-name" className="font-semibold text-sm">
							First Name
							<RequiredField />
						</Label>
						<Input
							type="text"
							name="first-name"
							id="first-name"
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							required
							readOnly={config?.isUtahComposeSite}
							maxLength={studentInfoResult?.rules.firstNameMaxLength}
							value={fName}
							onChange={(e) => setFName(e.target.value)}
						/>
					</Field>
					<Field>
						<Label htmlFor="last-name" className="font-semibold text-sm">
							Last Name
							<RequiredField />
						</Label>
						<Input
							type="text"
							name="last-name"
							id="last-name"
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							required
							readOnly={config?.isUtahComposeSite}
							maxLength={studentInfoResult?.rules.lastNameMaxLength}
							value={lName}
							onChange={(e) => setLName(e.target.value)}
						/>
					</Field>

					<Field>
						<Label htmlFor="external-student-id" className="font-semibold text-sm">
							{config?.writerExtIdLabel}
						</Label>
						<Input
							type="text"
							name="external-student-id"
							id="external-student-id"
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							required={!studentInfoResult?.isEditing}
							readOnly={studentInfoResult?.isEditing || config?.isUtahComposeSite}
							maxLength={studentInfoResult?.rules.externalIdMaxLength}
							value={externalId}
							onChange={(e) => setExternalId(e.target.value)}
						/>
					</Field>
					<Field>
						<Label htmlFor="dob" className="font-semibold text-sm">
							Date of Birth
							<RequiredField />
						</Label>
						<Input
							type="date"
							name="dob"
							id="dob"
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							required
							readOnly={config?.isUtahComposeSite}
							value={dateOfBirth}
							max={todayDate}
							onChange={(e) => setDateOfBirth(e.target.value)}
						/>
					</Field>

					<Field>
						<Label className="font-semibold text-sm">
							Grade
							<RequiredField />
						</Label>
						{config?.isUtahComposeSite ? (
							<Input
								type="text"
								id="grade"
								readOnly={true}
								className="w-full mt-1 h-[34px] rounded-lg border-none bg-gray-200 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
								value={gradeOptions.find((g) => g.value === grade)?.label}
							/>
						) : (
							<SimpleListbox
								id="gradeFilter"
								options={gradeOptions}
								value={grade ?? ""}
								className={
									"w-full mt-1 text-gray-900 focus:ring-1 focus:ring-inset focus:ring-primary-600 !py-1.5"
								}
								onChange={(e) => setGrade(Number(e.target.value))}
							/>
						)}
					</Field>
					<Field>
						<Label className="font-semibold text-sm">
							Scoring Level
							<RequiredField />
						</Label>
						<SimpleListbox
							id="scoringLevelFilter"
							options={scoringLevelOptions}
							value={scoringLevel ?? ""}
							className={
								"w-full mt-1 text-gray-900 focus:ring-1 focus:ring-inset focus:ring-primary-600 !py-1.5"
							}
							onChange={(e) => setScoringLevel(Number(e.target.value))}
						/>
					</Field>

					<Field className="col-span-2">
						<Label htmlFor="username" className="font-semibold text-sm">
							Username
							<RequiredField />
						</Label>
						<Input
							type="text"
							name="username"
							id="username"
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							readOnly={studentInfoResult?.isEditing}
							required={!studentInfoResult?.isEditing}
							maxLength={studentInfoResult?.rules.userNameMaxLength}
							value={userName}
							onChange={(e) => setUserName(e.target.value)}
						/>
					</Field>
					<Field className="col-span-2">
						<Label htmlFor="password" className="font-semibold text-sm">
							Password
							<RequiredField />
						</Label>
						<div className="flex gap-2 items-center">
							<div className="w-3/4 mt-1">
								<Password
									id="password"
									name="password"
									readonly={studentInfoResult?.isEditing}
									required={!studentInfoResult?.isEditing}
									className="block w-full rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={password}
								/>
							</div>
							{studentInfoResult && (
								<div className="min-w-1/4">
									<ResetStudentPasswordButton
										studentId={props.studentId}
										studentName={fName + " " + lName}
										rules={studentInfoResult.rules}
										setResetPasswordError={setResetPasswordError}
									/>
								</div>
							)}
						</div>
					</Field>
				</div>
			</div>
			{!config?.isUtahComposeSite && (
				<div className="mt-2">
					<div className="flex items-center">
						{showDemographicSection ? (
							<ChevronDownIcon
								tabIndex={0}
								className="size-5 hover:cursor-pointer"
								onClick={toggleShowDemographicSection}
								onKeyDown={(e) =>
									handleEnterSpaceKey(e, toggleShowDemographicSection)
								}
							/>
						) : (
							<ChevronRightIcon
								tabIndex={0}
								className="size-5 hover:cursor-pointer"
								onClick={toggleShowDemographicSection}
								onKeyDown={(e) =>
									handleEnterSpaceKey(e, toggleShowDemographicSection)
								}
							/>
						)}
						<h3 className="text-base font-semibold text-gray-900 pt-2 pb-2 ml-4">
							Demographic Information
						</h3>
					</div>
					{showDemographicSection && (
						<div className="pb-2 pt-1 grid grid-cols-1 gap-y-1 gap-x-3 sm:grid-cols-2">
							<Field>
								<Label htmlFor="ethnicity" className="font-semibold text-sm">
									Ethnicity
								</Label>
								<Input
									type="text"
									name="ethnicity"
									id="ethnicity"
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									readOnly={true}
									value={
										ethnicityOptions.find(
											(e) => e.value === (studentInfoResult?.ethnicity ?? "")
										)?.label
									}
								/>
							</Field>
							<Field>
								<Label htmlFor="gender" className="font-semibold text-sm">
									Gender
								</Label>
								<Input
									type="text"
									name="gender"
									id="gender"
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									readOnly={true}
									value={
										genderOptions.find(
											(g) => g.value === (studentInfoResult?.gender ?? "")
										)?.label
									}
								/>
							</Field>
							<Field>
								<Label
									htmlFor="economicallyDisadvantaged"
									className="font-semibold text-sm">
									Economically Disadvantaged
								</Label>
								<Input
									type="text"
									name="economicallyDisadvantaged"
									id="economicallyDisadvantaged"
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									readOnly={true}
									value={
										YesNoOptions.find(
											(ed) =>
												ed.value ===
												(studentInfoResult?.economicallyDisadvantaged?.toString() ??
													"")
										)?.label
									}
								/>
							</Field>
							<Field>
								<Label htmlFor="limitedEnglish" className="font-semibold text-sm">
									Limited English
								</Label>
								<Input
									type="text"
									name="limitedEnglish"
									id="limitedEnglish"
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									readOnly={true}
									value={
										YesNoOptions.find(
											(le) =>
												le.value ===
												(studentInfoResult?.limitedEnglish?.toString() ??
													"")
										)?.label
									}
								/>
							</Field>
						</div>
					)}
				</div>
			)}
		</section>
	);
}
